import type {
  BackupManifest,
  LocalAnalyticsEvent,
  LocalDocument,
  LocalDraft,
  LocalCompletion,
  LocalSignature,
  MyInfo,
  StorageDestination,
  Template,
  Workflow,
} from '@signbento/contracts'
import {
  blobSha256,
  decodeRecordPayload,
  encodeRecordPayload,
  type VaultCryptoBox,
} from './crypto-box.js'
import { StorageError } from './errors.js'
import type { BlobStore, RecordStore } from './stores.js'
import {
  BACKUP_MANIFEST_RECORD_ID,
  MY_INFO_RECORD_ID,
  compareRecordsForListing,
  recordKey,
  revisionCondition,
  type BlobRef,
  type CommitOptions,
  type CommitResult,
  type DocumentListOptions,
  type LocalVault,
  type SettingEntry,
  type StoredRecord,
  type VaultChange,
  type VaultChangeChannel,
  type VaultClock,
  type VaultRecordKind,
} from './types.js'

export interface CreateLocalVaultOptions {
  readonly vault_id: string
  readonly records: RecordStore
  readonly blobs: BlobStore
  readonly crypto: VaultCryptoBox
  readonly clock: VaultClock
  readonly channel?: VaultChangeChannel | null
}

interface ListedRecord<T> {
  readonly id: string
  readonly value: T
  readonly revision: number
  readonly committed_at: string
}

const textDecoder = new TextDecoder()
const COMPLETION_INDEX_MARKER = 'completion.indexed:v1'

export function createLocalVault(options: CreateLocalVaultOptions): LocalVault {
  const { records, blobs, crypto, clock } = options
  const channel = options.channel ?? null

  function publish(change: VaultChange): void {
    channel?.publish(change)
  }

  async function commit(
    kind: VaultRecordKind,
    id: string,
    value: unknown,
    commitOptions?: CommitOptions,
  ): Promise<CommitResult> {
    const key = recordKey(kind, id)
    const existing = await records.get(key)
    const revision = (existing?.revision ?? 0) + 1
    const committed_at = clock.now().toISOString()
    const payload = await crypto.sealRecord(kind, id, revision, encodeRecordPayload(value))
    await records.put(
      {
        key,
        kind,
        id,
        revision,
        committed_at,
        ...(kind === 'completion'
          ? { completion_document_id: (value as LocalCompletion).document_id }
          : {}),
        payload,
      },
      revisionCondition(commitOptions),
    )
    publish({ kind, id, revision, committed_at, deleted: false })
    return { revision, committed_at }
  }

  async function read<T>(kind: VaultRecordKind, id: string): Promise<T | null> {
    const record = await records.get(recordKey(kind, id))
    if (record === null) return null
    return decodeRecordPayload<T>(
      await crypto.openRecord(record.kind, record.id, record.revision, record.payload),
    )
  }

  async function listRecords<T>(kind: VaultRecordKind): Promise<ListedRecord<T>[]> {
    const stored = [...(await records.list(kind))].sort(compareRecordsForListing)
    return decryptAll<T>(stored)
  }

  /** Decrypts a bounded set of stored records, preserving the order the store returned them in. */
  async function decryptAll<T>(stored: readonly StoredRecord[]): Promise<ListedRecord<T>[]> {
    const listed: ListedRecord<T>[] = []
    for (const record of stored) {
      listed.push({
        id: record.id,
        value: decodeRecordPayload<T>(
          await crypto.openRecord(record.kind, record.id, record.revision, record.payload),
        ),
        revision: record.revision,
        committed_at: record.committed_at,
      })
    }
    return listed
  }

  async function list<T>(kind: VaultRecordKind): Promise<T[]> {
    return (await listRecords<T>(kind)).map((entry) => entry.value)
  }

  /**
   * The bounded read behind `listDocuments(options)`: the store seeks to `offset` and returns
   * at most `limit` records, and only those are decrypted.
   */
  async function listSlice<T>(kind: VaultRecordKind, options: DocumentListOptions): Promise<T[]> {
    const stored = await records.query({
      kind,
      ...(options.limit === undefined ? {} : { limit: options.limit }),
      ...(options.offset === undefined ? {} : { offset: options.offset }),
    })
    return (await decryptAll<T>(stored)).map((entry) => entry.value)
  }

  let completionIndexReady: Promise<void> | null = null
  async function ensureCompletionIndex(): Promise<void> {
    if (completionIndexReady !== null) return completionIndexReady
    completionIndexReady = (async () => {
      if ((await read<boolean>('setting', COMPLETION_INDEX_MARKER)) === true) return
      // Version 3's index cannot infer a document ID from an older encrypted payload. This
      // one-time pass adds only its opaque local ID to the record header; ciphertext, revision and
      // commit order remain exactly as stored. Subsequent reads use the bounded IDB index.
      for (const record of await records.list('completion')) {
        const completion = decodeRecordPayload<LocalCompletion>(
          await crypto.openRecord(record.kind, record.id, record.revision, record.payload),
        )
        if (typeof completion.document_id !== 'string' || completion.document_id.length === 0) {
          throw new StorageError('OP_STORAGE_CORRUPT_REVISION', 'completion document ID is missing')
        }
        if (record.completion_document_id === completion.document_id) continue
        if (record.completion_document_id !== undefined) {
          throw new StorageError('OP_STORAGE_CORRUPT_REVISION', 'completion index ID changed')
        }
        try {
          await records.put(
            { ...record, completion_document_id: completion.document_id },
            { kind: 'expected', revision: record.revision },
          )
        } catch (error) {
          // A different tab may have removed or updated the record after the legacy snapshot.
          // New writes carry the index header, so only an unresolved live row is an error.
          if ((error as { code?: string }).code !== 'OP_STORAGE_REVISION_CONFLICT') throw error
          const current = await records.get(record.key)
          if (current !== null && current.completion_document_id !== completion.document_id) {
            throw error
          }
        }
      }
      await commit('setting', COMPLETION_INDEX_MARKER, true)
    })().catch((error: unknown) => {
      completionIndexReady = null
      throw error
    })
    return completionIndexReady
  }

  async function remove(
    kind: VaultRecordKind,
    id: string,
    commitOptions?: CommitOptions,
  ): Promise<void> {
    const key = recordKey(kind, id)
    const existing = await records.get(key)
    if (existing === null) {
      if (commitOptions?.expected_revision !== undefined) {
        throw new StorageError('OP_STORAGE_REVISION_CONFLICT', `${key} no longer exists`)
      }
      return
    }
    await records.delete(key, revisionCondition(commitOptions))
    publish({
      kind,
      id,
      revision: existing.revision + 1,
      committed_at: clock.now().toISOString(),
      deleted: true,
    })
  }

  /** Restore/replay of the same logical event is a no-op; different bytes conflict. */
  async function appendAnalyticsEvent(event: LocalAnalyticsEvent): Promise<CommitResult> {
    const key = recordKey('analytics_event', event.event_id)
    const existing = await records.get(key)
    if (existing !== null) {
      const existingBytes = await crypto.openRecord(
        existing.kind,
        existing.id,
        existing.revision,
        existing.payload,
      )
      if (textDecoder.decode(existingBytes) === textDecoder.decode(encodeRecordPayload(event))) {
        return { revision: existing.revision, committed_at: existing.committed_at }
      }
      throw new StorageError(
        'OP_STORAGE_REVISION_CONFLICT',
        `analytics event ${event.event_id} already exists with different content`,
        { current_revision: existing.revision },
      )
    }
    return commit('analytics_event', event.event_id, event)
  }

  return {
    putDocument: (record, commitOptions) => commit('document', record.id, record, commitOptions),
    getDocument: (id) => read<LocalDocument>('document', id),
    listDocuments: (options) =>
      options === undefined
        ? list<LocalDocument>('document')
        : listSlice<LocalDocument>('document', options),
    countDocuments: () => records.count('document'),
    deleteDocument: (id, commitOptions) => remove('document', id, commitOptions),

    putDraft: (record, commitOptions) => commit('draft', record.id, record, commitOptions),
    getDraft: (id) => read<LocalDraft>('draft', id),
    listDrafts: (options) =>
      options === undefined ? list<LocalDraft>('draft') : listSlice<LocalDraft>('draft', options),
    deleteDraft: (id, commitOptions) => remove('draft', id, commitOptions),

    putTemplate: (record, commitOptions) => commit('template', record.id, record, commitOptions),
    getTemplate: (id) => read<Template>('template', id),
    listTemplates: () => list<Template>('template'),
    deleteTemplate: (id, commitOptions) => remove('template', id, commitOptions),

    putWorkflow: (record, commitOptions) => commit('workflow', record.id, record, commitOptions),
    getWorkflow: (id) => read<Workflow>('workflow', id),
    listWorkflows: () => list<Workflow>('workflow'),
    deleteWorkflow: (id, commitOptions) => remove('workflow', id, commitOptions),

    putSignature: (record, commitOptions) => commit('signature', record.id, record, commitOptions),
    getSignature: (id) => read<LocalSignature>('signature', id),
    listSignatures: () => list<LocalSignature>('signature'),
    deleteSignature: (id, commitOptions) => remove('signature', id, commitOptions),

    putCompletion: (record, commitOptions) =>
      commit('completion', record.id, record, commitOptions),
    getCompletion: (id) => read<LocalCompletion>('completion', id),
    listCompletions: (options) =>
      options === undefined
        ? list<LocalCompletion>('completion')
        : listSlice<LocalCompletion>('completion', options),
    latestCompletionForDocument: async (documentId) => {
      await ensureCompletionIndex()
      const stored = await records.latestCompletionForDocument(documentId)
      if (stored === null) return null
      const completion = decodeRecordPayload<LocalCompletion>(
        await crypto.openRecord(stored.kind, stored.id, stored.revision, stored.payload),
      )
      if (completion.document_id !== documentId) {
        throw new StorageError('OP_STORAGE_CORRUPT_REVISION', 'completion index points elsewhere')
      }
      return completion
    },
    deleteCompletion: (id, commitOptions) => remove('completion', id, commitOptions),

    putMyInfo: (record, commitOptions) =>
      commit('my_info', MY_INFO_RECORD_ID, record, commitOptions),
    getMyInfo: () => read<MyInfo>('my_info', MY_INFO_RECORD_ID),

    putSetting: (key, value, commitOptions) => commit('setting', key, value, commitOptions),
    getSetting: <T = unknown>(key: string) => read<T>('setting', key),
    listSettings: async (): Promise<SettingEntry[]> =>
      (await listRecords<unknown>('setting')).map((entry) => ({
        key: entry.id,
        value: entry.value,
      })),

    appendAnalyticsEvent,
    listAnalyticsEvents: async () =>
      (await list<LocalAnalyticsEvent>('analytics_event')).sort((a, b) =>
        a.occurred_at !== b.occurred_at
          ? a.occurred_at < b.occurred_at
            ? -1
            : 1
          : a.event_id.localeCompare(b.event_id),
      ),

    putBackupManifest: (record, commitOptions) =>
      commit('backup_manifest', BACKUP_MANIFEST_RECORD_ID, record, commitOptions),
    getBackupManifest: () => read<BackupManifest>('backup_manifest', BACKUP_MANIFEST_RECORD_ID),

    putStorageDestination: (record, commitOptions) =>
      commit('storage_destination', record.id, record, commitOptions),
    getStorageDestination: (id) => read<StorageDestination>('storage_destination', id),
    listStorageDestinations: () => list<StorageDestination>('storage_destination'),
    deleteStorageDestination: (id, commitOptions) =>
      remove('storage_destination', id, commitOptions),

    getRecordRevision: async (kind, id) => {
      const record = await records.get(recordKey(kind, id))
      return record?.revision ?? null
    },

    putBlob: async (key, bytes): Promise<BlobRef> => {
      if (await blobs.has(key)) {
        throw new StorageError(
          'OP_STORAGE_REVISION_CONFLICT',
          `blob ${key} already exists; blobs are immutable`,
        )
      }
      const sealed = await crypto.sealBlob(key, bytes)
      await blobs.put(key, sealed)
      return { key, byte_length: bytes.byteLength, sha256: await blobSha256(bytes) }
    },
    getBlob: async (key) => {
      const stored = await blobs.get(key)
      if (stored === null) {
        throw new StorageError('OP_STORAGE_MISSING_BYTES', `blob ${key} is missing locally`)
      }
      return crypto.openBlob(key, stored)
    },
    hasBlob: (key) => blobs.has(key),
    deleteBlob: (key) => blobs.delete(key),
    listBlobKeys: () => blobs.list(),
  }
}
